// Translation strings for Portuguese and English

export type Language = 'en' | 'pt';

export const translations = {
  en: {
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      download: 'Download',
      upload: 'Upload',
      submit: 'Submit',
      approve: 'Approve',
      reject: 'Reject',
      pending: 'Pending',
      completed: 'Completed',
      processing: 'Processing...',
      noData: 'No data available',
      search: 'Search',
      filter: 'Filter',
      refresh: 'Refresh',
      close: 'Close',
      yes: 'Yes',
      no: 'No',
      administrator: 'Administrator',
    },
    // Navigation
    nav: {
      home: 'Home',
      dashboard: 'Dashboard',
      mainMenu: 'Main Menu',
      processingSteps: '🚀 Processes',
      management: 'Management',
      step1: 'Structured Data',
      step2: 'Semi-Structured Data',
      step3: 'Unstructured Data',
      step4: 'Image & Multimedia Data',
      documentReview: 'Document Review',
      exceptions: 'Exceptions',
      documentUpload: 'Document Upload',
      exceptionQueue: 'Exception Queue',
      unloadingReceipt: 'Unloading Receipt',
      palletReconciliation: 'Pallet Reconciliation',
      vehicleRegistration: 'Vehicle Registration',
      settings: 'Settings',
      searchPlaceholder: 'Search documents... (Press Enter)',
      notifications: 'Notifications',
    },
    // Welcome Page
    welcome: {
      title: 'KrElixir OCR Intelligence Platform',
      subtitle: 'Intelligent Document Processing Platform',
      description: 'Advanced OCR and AI-powered document processing for logistics and supply chain management',
      enterApp: 'Enter Application',
      features: {
        ocr: 'Advanced OCR',
        ocrDesc: 'State-of-the-art optical character recognition',
        ai: 'AI Analysis',
        aiDesc: 'Intelligent document classification and extraction',
        automation: 'Automation',
        automationDesc: 'Streamlined workflow automation',
      },
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      subtitle: 'Overview of document processing activities',
      totalDocuments: 'Total Documents',
      pendingReview: 'Pending Review',
      approved: 'Approved',
      rejected: 'Rejected',
      recentActivity: 'Recent Activity',
      quickActions: 'Quick Actions',
      uploadDocument: 'Upload Document',
      viewExceptions: 'View Exceptions',
      generateReport: 'Generate Report',
      processingStats: 'Processing Statistics',
      todayProcessed: 'Processed Today',
      weeklyTrend: 'Weekly Trend',
      successRate: 'Success Rate',
      processingProgress: 'Processing Progress',
      todayStatus: "Today's document processing status",
      onTrack: 'On Track',
      dailyFeePayment: 'Structured Data',
      unloadingPayment: 'Semi-Structured Data',
      palletReconciliation: 'Unstructured Data',
      vehicleRegistration: 'Image & Multimedia Data',
    },
    // Document Upload
    documentUpload: {
      title: 'Document Upload',
      subtitle: 'Upload and process logistics documents',
      dragDrop: 'Drag & drop your document here',
      orClick: 'or click to browse',
      supportedFormats: 'PDF, JPG, PNG supported',
      deliveryNumber: 'Delivery Number',
      deliveryNumberPlaceholder: 'Enter delivery number (optional)',
      uploadButton: 'Upload & Process Document',
      processing: 'Processing...',
      extractedFields: 'Extracted Fields',
      totalFields: 'Total Fields',
      language: 'Language',
      originalText: 'Original Text',
      englishText: 'English Text',
      normalizedValue: 'Normalized Value',
      downloadLog: 'Download Processing Log',
    },
    // Exception Queue
    exceptionQueue: {
      title: 'Exception Queue',
      subtitle: 'Review and resolve document exceptions',
      documentId: 'Document ID',
      filename: 'Filename',
      status: 'Status',
      uploadedAt: 'Uploaded At',
      actions: 'Actions',
      approveConfirm: 'Are you sure you want to approve this document?',
      rejectConfirm: 'Are you sure you want to reject this document?',
      rejectReason: 'Rejection Reason',
    },
    // Unloading Receipt
    unloadingReceipt: {
      title: 'Unloading Receipt Upload',
      subtitle: 'Process unloading payment receipts',
      providerName: 'Provider Name',
      helperName: 'Helper Name',
      amount: 'Amount',
      date: 'Date',
      invoiceNumber: 'Invoice Number',
      validation: 'Validation',
      statusRecommendation: 'Status Recommendation',
      confidence: 'Confidence',
      rulesApplied: 'Rules Applied',
      flags: 'Flags',
    },
    // Pallet Reconciliation
    palletReconciliation: {
      title: 'Pallet Voucher Management',
      subtitle: 'Upload vouchers, run reconciliation, and generate reports',
      uploadVoucher: 'Upload Voucher',
      emailImport: 'Email Import',
      reconciliation: 'Reconciliation',
      dragDropVoucher: 'Drag & drop your pallet voucher here',
      uploadProcess: 'Upload & Process Voucher',
      processingSteps: 'Processing Steps',
      palletSummary: 'Pallet Voucher Summary',
      pallets: 'Pallets',
      status: 'Status',
      customer: 'Customer',
      date: 'Date',
      extractedFields: 'Extracted Fields',
      runReconciliation: 'Run Pallet Reconciliation',
      reconciliationDesc: 'Analyze all uploaded vouchers, compare with company inventory, detect divergences, and generate comprehensive reports.',
      recipientEmail: 'Recipient Email (for report)',
      runAnalysis: 'Run Reconciliation',
      runningAnalysis: 'Running Analysis...',
      reconciliationReport: 'Reconciliation Report',
      vouchers: 'Vouchers',
      totalPallets: 'Total Pallets',
      returned: 'Returned',
      withCustomer: 'With Customer',
      divergence: 'Divergence',
      financialImpact: 'Financial Impact',
      aiAnalysis: 'AI Analysis',
      riskLevel: 'Risk Level',
      keyFindings: 'Key Findings',
      recommendedActions: 'Recommended Actions',
      previewEmailReport: 'Preview Email Report',
      hideEmailPreview: 'Hide Email Preview',
      // Email Import
      importFromEmail: 'Import Vouchers from Email',
      senderEmail: 'Sender Email',
      subject: 'Subject',
      emailBody: 'Email Body',
      selectAttachments: 'Select Attachments',
      filesSelected: 'file(s) selected',
      processEmailVouchers: 'Process Email Vouchers',
    },
    // Vehicle Registration
    vehicleRegistration: {
      title: 'Vehicle Registration',
      subtitle: 'Register and manage carrier vehicles',
      licensePlate: 'License Plate',
      carrierName: 'Carrier Name',
      vehicleType: 'Vehicle Type',
      model: 'Model',
      capacity: 'Capacity',
    },
    // Footer
    footer: {
      poweredBy: 'Powered by',
      allRightsReserved: 'All rights reserved',
      companyDesc: 'Enabling Digital Transformation Through Data and Cloud Solutions. Enterprise-grade OCR and AI for logistics.',
      product: 'Product',
      productLinks: { features: 'Features', howItWorks: 'How It Works', pricing: 'Pricing', demo: 'Request Demo' },
      resources: 'Resources',
      resourceLinks: { docs: 'Documentation', api: 'API Reference', support: 'Support', blog: 'Blog' },
      contact: 'Contact',
      contactEmail: 'info@krelixir.com',
      contactPhone: '+55 (11) 9999-0000',
      followUs: 'Follow Us',
      tagline: 'Enabling Digital Transformation Through Data and Cloud Solutions',
    },
    // Theme
    theme: {
      light: 'Light Mode',
      dark: 'Dark Mode',
    },
    // Language
    language: {
      select: 'Select Language',
      english: 'English',
      portuguese: 'Portuguese',
    },
    // Landing Page
    landing: {
      hero: {
        badge: 'AI-Powered Document Intelligence',
        title: 'Transform Your Enterprise Documents|with Agentic AI-Powered OCR',
        subtitle: 'Enterprise-grade OCR platform that automates document processing, eliminates manual data entry, and accelerates your logistics operations.',
        cta: 'Get Started',
        ctaSecondary: 'Learn More',
        highlights: {
          security: 'Enterprise Security',
          processing: 'Real-time Processing',
          accuracy: '99.5% Accuracy',
        },
      },
      features: {
        sectionTitle: 'Powerful Features',
        sectionSubtitle: 'Everything you need to digitize and automate your document workflows',
        docAi: {
          title: 'Structured Data',
          desc: 'Process databases, Excel sheets, and ERP/CRM records with automated field mapping and validation.',
          highlights: ['Databases', 'Excel sheets', 'ERP/CRM records'],
        },
        pallet: {
          title: 'Semi-Structured Data',
          desc: 'Parse JSON, XML, emails, and log files with intelligent schema detection and data extraction.',
          highlights: ['JSON', 'XML', 'Emails / Log files'],
        },
        vehicle: {
          title: 'Unstructured Data',
          desc: 'Extract insights from PDFs, contracts, text documents, and chat transcripts using advanced NLP.',
          highlights: ['PDFs / Contracts', 'Text documents', 'Chat transcripts'],
        },
        exceptions: {
          title: 'Image & Multimedia',
          desc: 'AI-powered recognition for scanned docs, photos, and media — with built-in correction for low DPI, skewed scans, phone camera shots, and faded prints.',
          highlights: ['Scanned documents / Photos', 'Medical images / Videos / Audio', 'Low DPI & skewed scan correction', 'Phone camera & faded print recovery'],
        },
        connectors: { title: 'Connect Your Data Source', desc: 'Plug into your existing enterprise systems. Native connectors for Salesforce, SAP, GCP, AWS, Azure, and more — no custom integration required.' },
      },
      howItWorks: {
        sectionTitle: 'How It Works',
        sectionSubtitle: 'Three simple steps to transform your document processing',
        step1: { title: 'Upload', desc: 'Drag and drop your logistics documents — invoices, receipts, vouchers, registrations.' },
        step2: { title: 'AI Processes', desc: 'Our AI engine extracts, classifies, and validates all data fields automatically.' },
        step3: { title: 'Review & Export', desc: 'Review results, handle exceptions, and export structured data to your systems.' },
      },
      stats: {
        accuracy: '99.5%',
        accuracyLabel: 'Extraction Accuracy',
        speed: '10x',
        speedLabel: 'Faster Processing',
        processes: '4',
        processesLabel: 'Automated Processes',
        documents: '24/7',
        documentsLabel: 'Real-Time Monitoring',
      },
      cta: {
        title: 'Ready to Transform Your Document Processing?',
        subtitle: 'Start processing your logistics documents with AI-powered accuracy today.',
        button: 'Start Processing Documents',
      },
      techStrip: {
        poweredBy: 'Powered by',
      },
      automatedProcesses: {
        label: 'Automated Processes',
        structured: 'Structured Data Processing',
        semiStructured: 'Semi-Structured Data Parsing',
        unstructured: 'Unstructured Data Extraction',
        multimedia: 'Image & Multimedia Analysis',
      },
      whyChooseUs: {
        chip: 'Why KrElixir',
        title: 'Built for Enterprise Logistics',
        subtitle: 'Purpose-built for JBS and enterprise logistics operations. Our platform handles the complexity of multi-format document processing at scale, with built-in compliance and audit capabilities.',
        cta: 'Start processing documents',
        benefit1Title: 'Structured Data Output',
        benefit1Desc: 'Extracted data is validated, structured, and ready for ERP integration — no manual cleanup.',
        benefit2Title: 'Real-time Analytics',
        benefit2Desc: 'Live dashboards track processing volume, accuracy rates, and exception trends.',
        benefit3Title: 'API-first Architecture',
        benefit3Desc: 'RESTful APIs for seamless integration with SAP, Oracle, and custom systems.',
        benefit4Title: 'SOC 2 Ready',
        benefit4Desc: 'Google Cloud infrastructure with encryption at rest and in transit. Full audit logging.',
      },
      howItWorksAccuracy: {
        label: 'Extraction Accuracy',
        poweredBy: 'powered by Google Document AI',
      },
    },
    // Navbar
    navbar: {
      features: 'Features',
      howItWorks: 'How It Works',
      about: 'About',
      login: 'Login',
      getStarted: 'Get Started',
    },
    // Auth
    auth: {
      loginTitle: 'Welcome Back',
      loginSubtitle: 'Sign in to your KrElixir account',
      signupTitle: 'Create Account',
      signupSubtitle: 'Start your document processing journey',
      email: 'Email Address',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      displayName: 'Full Name',
      loginButton: 'Sign In',
      signupButton: 'Create Account',
      forgotPassword: 'Forgot Password?',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      signUp: 'Sign Up',
      signIn: 'Sign In',
      resetTitle: 'Reset Password',
      resetSubtitle: 'Enter your email and we will send you a reset link',
      resetButton: 'Send Reset Link',
      resetSuccess: 'Password reset email sent! Check your inbox.',
      backToLogin: 'Back to Login',
      logout: 'Logout',
      errors: {
        invalidEmail: 'Please enter a valid email address.',
        weakPassword: 'Password must be at least 6 characters.',
        passwordMismatch: 'Passwords do not match.',
        emailInUse: 'This email is already registered.',
        userNotFound: 'No account found with this email.',
        wrongPassword: 'Incorrect password. Please try again.',
        generic: 'An error occurred. Please try again.',
        nameRequired: 'Please enter your full name.',
      },
    },
    // Document Upload Page (Process 1)
    docUploadPage: {
      selectFromFirebase: 'Select from Firebase',
      chooseExistingDoc: 'Choose a previously uploaded document to reprocess',
      refreshDocList: 'Refresh document list',
      loadingDocs: 'Loading documents...',
      selectADocument: 'Select a document',
      noneUploadNew: '— None (upload a new file) —',
      reprocessing: 'Reprocessing...',
      reprocessWithERP: 'Reprocess with ERP Comparison',
      reprocessDesc: 'Runs ERP comparison & classification on the existing extracted data',
      orUploadNew: 'OR UPLOAD NEW',
      deliveryNumber: 'Delivery Number',
      deliveryNumberPlaceholder: 'Enter delivery number (optional)',
      dropFileHere: 'Drop your file here, or browse',
      supportedFormats: 'Supports PDF, JPG, JPEG, PNG — Stored in Firebase',
      removeFile: 'Remove file',
      uploadingToFirebase: 'Uploading to Firebase...',
      uploadAndExtract: 'Upload & Extract',
      processDocument: 'Process Document',
    },
    // Exception Queue Page
    exceptionQueuePage: {
      title: 'Exception Queue',
      subtitle: 'Review and process documents requiring manual attention',
      pending: 'pending',
      noExceptions: 'No exception cases found. All documents processed successfully!',
      documentId: 'Document ID',
      deliveryNumber: 'Delivery Number',
      filename: 'Filename',
      exceptionReasons: 'Exception Reasons',
      uploadedAt: 'Uploaded At',
      actions: 'Actions',
      review: 'Review',
      more: 'more',
      reviewExceptionCase: 'Review Exception Case',
      comparisonResults: 'Comparison Results',
      field: 'Field',
      document: 'Document',
      erp: 'ERP',
      match: 'Match',
      approving: 'Approving...',
      rejecting: 'Rejecting...',
      rejectDailyFee: 'Reject Daily Fee',
      rejectionReason: 'Rejection Reason',
      confirmReject: 'Confirm Reject',
    },
    // Document Review Page
    documentReviewPage: {
      title: 'Document Review',
      filterByStatus: 'Filter by Status',
      all: 'All',
      noDocuments: 'No documents found',
      documentId: 'Document ID',
      filename: 'Filename',
      deliveryNumber: 'Delivery Number',
      status: 'Status',
      uploadedAt: 'Uploaded At',
      classification: 'Classification',
      exception: 'Exception',
      standard: 'Standard',
      documentDetails: 'Document Details',
      basicInfo: 'Basic Information',
      extractedData: 'Extracted Data',
      comparisonResults: 'Comparison Results',
      exceptionCase: 'Exception Case',
      standardCase: 'Standard Case',
      rejectDailyFee: 'Reject Daily Fee',
      rejectionReason: 'Rejection Reason',
    },
    // In-App Footer
    appFooter: {
      allRightsReserved: 'All rights reserved',
      tagline: 'Enabling Digital Transformation Through Data and Cloud Solutions',
      website: 'Website',
      cloudSolutions: 'Cloud Solutions',
      dataSolutions: 'Data Solutions',
    },
    // Unloading Receipt Page (Process 2)
    receiptPage: {
      title: 'Unloading Receipt Upload',
      subtitle: 'Step 2: Upload and validate unloading payment receipts',
      orderNumber: 'Order Number',
      orderNumberPlaceholder: 'Enter order number (optional)',
      dropReceipt: 'Drop your receipt here',
      dragDropReceipt: 'Drag & drop your receipt here',
      browseFormats: 'or click to browse • PDF, JPG, PNG supported',
      uploading: 'Uploading...',
      uploadReceipt: 'Upload Receipt',
      processReceipt: 'Process Receipt',
      processingSteps: 'Processing Steps',
      validationResult: 'Validation Result',
      valid: 'Valid',
      needsReview: 'Needs Review',
      issues: 'Issues',
      missingFields: 'Missing fields',
      statusRecommendation: 'Status Recommendation',
      confidence: 'Confidence',
      rulesApplied: 'Rules Applied',
      reasoning: 'Reasoning',
      approveReceipt: 'Approve Receipt',
      rejectReceipt: 'Reject Receipt',
      extractedData: 'Extracted Data',
      fields: 'fields',
    },
    // Pallet Voucher Page (Process 3)
    palletPage: {
      title: 'Pallet Voucher Management',
      subtitle: 'Step 3: Upload vouchers, run reconciliation, and generate reports',
      dragDropVoucher: 'Drag & drop your pallet voucher here',
      dropVoucher: 'Drop your voucher here',
      browseFormats: 'or click to browse • PDF, JPG, PNG supported',
      uploadProcess: 'Upload & Process Voucher',
    },
    // Vehicle Registration Page (Process 4)
    vehiclePage: {
      title: 'Carrier Vehicle Registration',
      subtitle: 'Upload vehicle photos or documents for automatic analysis. The system will extract vehicle characteristics using computer vision and compare with ERP registration data.',
      photoMode: 'Photo Mode (Computer Vision)',
      documentMode: 'Document Mode (OCR)',
      licensePlate: 'License Plate (Optional - for ERP lookup)',
      licensePlateHelper: 'Enter known plate to compare with ERP registration',
      selectPhoto: 'Select Vehicle Photo or Document',
      analyzeVehicle: 'Analyze Vehicle',
      analyzing: 'Analyzing...',
    },
  },
  pt: {
    // Common
    common: {
      loading: 'Carregando...',
      error: 'Erro',
      success: 'Sucesso',
      cancel: 'Cancelar',
      save: 'Salvar',
      delete: 'Excluir',
      edit: 'Editar',
      view: 'Visualizar',
      download: 'Baixar',
      upload: 'Enviar',
      submit: 'Enviar',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      pending: 'Pendente',
      completed: 'Concluído',
      processing: 'Processando...',
      noData: 'Nenhum dado disponível',
      search: 'Buscar',
      filter: 'Filtrar',
      refresh: 'Atualizar',
      close: 'Fechar',
      yes: 'Sim',
      no: 'Não',
      administrator: 'Administrador',
    },
    // Navigation
    nav: {
      home: 'Início',
      dashboard: 'Painel',
      mainMenu: 'Menu Principal',
      processingSteps: '🚀 Processos Operacionais',
      management: 'Gerenciamento',
      step1: 'Dados Estruturados',
      step2: 'Dados Semiestruturados',
      step3: 'Dados Não Estruturados',
      step4: 'Imagem e Multimídia',
      documentReview: 'Revisão de Documentos',
      exceptions: 'Exceções',
      documentUpload: 'Upload de Documento',
      exceptionQueue: 'Fila de Exceções',
      unloadingReceipt: 'Recibo de Descarga',
      palletReconciliation: 'Reconciliação de Paletes',
      vehicleRegistration: 'Registro de Veículo',
      settings: 'Configurações',
      searchPlaceholder: 'Buscar documentos... (Pressione Enter)',
      notifications: 'Notificações',
    },
    // Welcome Page
    welcome: {
      title: 'KrElixir OCR Intelligence Platform',
      subtitle: 'Plataforma Inteligente de Processamento de Documentos',
      description: 'OCR avançado e processamento de documentos com IA para logística e gestão da cadeia de suprimentos',
      enterApp: 'Entrar na Aplicação',
      features: {
        ocr: 'OCR Avançado',
        ocrDesc: 'Reconhecimento óptico de caracteres de última geração',
        ai: 'Análise de IA',
        aiDesc: 'Classificação e extração inteligente de documentos',
        automation: 'Automação',
        automationDesc: 'Automação de fluxo de trabalho simplificada',
      },
    },
    // Dashboard
    dashboard: {
      title: 'Painel',
      subtitle: 'Visão geral das atividades de processamento de documentos',
      totalDocuments: 'Total de Documentos',
      pendingReview: 'Pendente de Revisão',
      approved: 'Aprovados',
      rejected: 'Rejeitados',
      recentActivity: 'Atividade Recente',
      quickActions: 'Ações Rápidas',
      uploadDocument: 'Enviar Documento',
      viewExceptions: 'Ver Exceções',
      generateReport: 'Gerar Relatório',
      processingStats: 'Estatísticas de Processamento',
      todayProcessed: 'Processados Hoje',
      weeklyTrend: 'Tendência Semanal',
      successRate: 'Taxa de Sucesso',
      processingProgress: 'Progresso de Processamento',
      todayStatus: 'Status de processamento de documentos de hoje',
      onTrack: 'No Caminho',
      dailyFeePayment: 'Dados Estruturados',
      unloadingPayment: 'Dados Semiestruturados',
      palletReconciliation: 'Dados Não Estruturados',
      vehicleRegistration: 'Imagem e Multimídia',
    },
    // Document Upload
    documentUpload: {
      title: 'Upload de Documento',
      subtitle: 'Envie e processe documentos logísticos',
      dragDrop: 'Arraste e solte seu documento aqui',
      orClick: 'ou clique para navegar',
      supportedFormats: 'PDF, JPG, PNG suportados',
      deliveryNumber: 'Número de Entrega',
      deliveryNumberPlaceholder: 'Digite o número de entrega (opcional)',
      uploadButton: 'Enviar e Processar Documento',
      processing: 'Processando...',
      extractedFields: 'Campos Extraídos',
      totalFields: 'Total de Campos',
      language: 'Idioma',
      originalText: 'Texto Original',
      englishText: 'Texto em Inglês',
      normalizedValue: 'Valor Normalizado',
      downloadLog: 'Baixar Log de Processamento',
    },
    // Exception Queue
    exceptionQueue: {
      title: 'Fila de Exceções',
      subtitle: 'Revise e resolva exceções de documentos',
      documentId: 'ID do Documento',
      filename: 'Nome do Arquivo',
      status: 'Status',
      uploadedAt: 'Enviado em',
      actions: 'Ações',
      approveConfirm: 'Tem certeza que deseja aprovar este documento?',
      rejectConfirm: 'Tem certeza que deseja rejeitar este documento?',
      rejectReason: 'Motivo da Rejeição',
    },
    // Unloading Receipt
    unloadingReceipt: {
      title: 'Upload de Recibo de Descarga',
      subtitle: 'Processe recibos de pagamento de descarga',
      providerName: 'Nome do Fornecedor',
      helperName: 'Nome do Ajudante',
      amount: 'Valor',
      date: 'Data',
      invoiceNumber: 'Número da Nota Fiscal',
      validation: 'Validação',
      statusRecommendation: 'Recomendação de Status',
      confidence: 'Confiança',
      rulesApplied: 'Regras Aplicadas',
      flags: 'Alertas',
    },
    // Pallet Reconciliation
    palletReconciliation: {
      title: 'Gestão de Vale Palete',
      subtitle: 'Envie vales, execute reconciliação e gere relatórios',
      uploadVoucher: 'Enviar Vale',
      emailImport: 'Importar Email',
      reconciliation: 'Reconciliação',
      dragDropVoucher: 'Arraste e solte seu vale palete aqui',
      uploadProcess: 'Enviar e Processar Vale',
      processingSteps: 'Etapas de Processamento',
      palletSummary: 'Resumo do Vale Palete',
      pallets: 'Paletes',
      status: 'Status',
      customer: 'Cliente',
      date: 'Data',
      extractedFields: 'Campos Extraídos',
      runReconciliation: 'Executar Reconciliação de Paletes',
      reconciliationDesc: 'Analise todos os vales enviados, compare com o inventário da empresa, detecte divergências e gere relatórios abrangentes.',
      recipientEmail: 'Email do Destinatário (para relatório)',
      runAnalysis: 'Executar Reconciliação',
      runningAnalysis: 'Executando Análise...',
      reconciliationReport: 'Relatório de Reconciliação',
      vouchers: 'Vales',
      totalPallets: 'Total de Paletes',
      returned: 'Devolvidos',
      withCustomer: 'Com Cliente',
      divergence: 'Divergência',
      financialImpact: 'Impacto Financeiro',
      aiAnalysis: 'Análise de IA',
      riskLevel: 'Nível de Risco',
      keyFindings: 'Principais Descobertas',
      recommendedActions: 'Ações Recomendadas',
      previewEmailReport: 'Visualizar Relatório por Email',
      hideEmailPreview: 'Ocultar Visualização de Email',
      // Email Import
      importFromEmail: 'Importar Vales de Email',
      senderEmail: 'Email do Remetente',
      subject: 'Assunto',
      emailBody: 'Corpo do Email',
      selectAttachments: 'Selecionar Anexos',
      filesSelected: 'arquivo(s) selecionado(s)',
      processEmailVouchers: 'Processar Vales de Email',
    },
    // Vehicle Registration
    vehicleRegistration: {
      title: 'Registro de Veículo',
      subtitle: 'Registre e gerencie veículos de transportadoras',
      licensePlate: 'Placa',
      carrierName: 'Nome da Transportadora',
      vehicleType: 'Tipo de Veículo',
      model: 'Modelo',
      capacity: 'Capacidade',
    },
    // Footer
    footer: {
      poweredBy: 'Desenvolvido por',
      allRightsReserved: 'Todos os direitos reservados',
      companyDesc: 'Transformação Digital Através de Dados e Soluções em Nuvem. OCR e IA de nível empresarial para logística.',
      product: 'Produto',
      productLinks: { features: 'Funcionalidades', howItWorks: 'Como Funciona', pricing: 'Preços', demo: 'Solicitar Demo' },
      resources: 'Recursos',
      resourceLinks: { docs: 'Documentação', api: 'Referência da API', support: 'Suporte', blog: 'Blog' },
      contact: 'Contato',
      contactEmail: 'info@krelixir.com',
      contactPhone: '+55 (11) 9999-0000',
      followUs: 'Siga-nos',
      tagline: 'Transformação Digital Através de Dados e Soluções em Nuvem',
    },
    // Theme
    theme: {
      light: 'Modo Claro',
      dark: 'Modo Escuro',
    },
    // Language
    language: {
      select: 'Selecionar Idioma',
      english: 'Inglês',
      portuguese: 'Português',
    },
    // Landing Page
    landing: {
      hero: {
        badge: 'Inteligência Documental com IA',
        title: 'Transforme os Documentos da Sua Empresa|com OCR Impulsionado por IA Agêntica',
        subtitle: 'Plataforma OCR de nível empresarial que automatiza o processamento de documentos, elimina a entrada manual de dados e acelera suas operações logísticas.',
        cta: 'Começar Agora',
        ctaSecondary: 'Saiba Mais',
        highlights: {
          security: 'Segurança Empresarial',
          processing: 'Processamento em Tempo Real',
          accuracy: '99,5% de Precisão',
        },
      },
      features: {
        sectionTitle: 'Funcionalidades Poderosas',
        sectionSubtitle: 'Tudo que você precisa para digitalizar e automatizar seus fluxos de documentos',
        docAi: {
          title: 'Dados Estruturados',
          desc: 'Processe bancos de dados, planilhas Excel e registros ERP/CRM com mapeamento e validação automáticos.',
          highlights: ['Bancos de dados', 'Planilhas Excel', 'Registros ERP/CRM'],
        },
        pallet: {
          title: 'Dados Semiestruturados',
          desc: 'Analise JSON, XML, e-mails e arquivos de log com detecção inteligente de esquema e extração de dados.',
          highlights: ['JSON', 'XML', 'E-mails / Arquivos de log'],
        },
        vehicle: {
          title: 'Dados Não Estruturados',
          desc: 'Extraia insights de PDFs, contratos, documentos de texto e transcrições de chat usando NLP avançado.',
          highlights: ['PDFs / Contratos', 'Documentos de texto', 'Transcrições de chat'],
        },
        exceptions: {
          title: 'Imagem e Multimídia',
          desc: 'Analise documentos digitalizados, fotos, imagens médicas, vídeos e arquivos de áudio com reconhecimento por IA.',
          highlights: ['Documentos digitalizados / Fotos', 'Imagens médicas / Vídeos / Áudio', 'Correção de DPI baixo e scan torto', 'Recuperação de câmera de celular e impressão desbotada'],
        },
        connectors: { title: 'Conecte Sua Fonte de Dados', desc: 'Integre-se aos seus sistemas empresariais existentes. Conectores nativos para Salesforce, SAP, GCP, AWS, Azure e mais — sem integração personalizada.' },
      },
      howItWorks: {
        sectionTitle: 'Como Funciona',
        sectionSubtitle: 'Três passos simples para transformar seu processamento de documentos',
        step1: { title: 'Envie', desc: 'Arraste e solte seus documentos logísticos — faturas, recibos, vales, registros.' },
        step2: { title: 'IA Processa', desc: 'Nosso motor de IA extrai, classifica e valida todos os campos de dados automaticamente.' },
        step3: { title: 'Revise e Exporte', desc: 'Revise resultados, trate exceções e exporte dados estruturados para seus sistemas.' },
      },
      stats: {
        accuracy: '99,5%',
        accuracyLabel: 'Precisão de Extração',
        speed: '10x',
        speedLabel: 'Processamento Mais Rápido',
        processes: '4',
        processesLabel: 'Processos Automatizados',
        documents: '24/7',
        documentsLabel: 'Monitoramento em Tempo Real',
      },
      cta: {
        title: 'Pronto para Transformar Seu Processamento de Documentos?',
        subtitle: 'Comece a processar seus documentos logísticos com precisão de IA hoje.',
        button: 'Começar a Processar Documentos',
      },
      techStrip: {
        poweredBy: 'Desenvolvido por',
      },
      automatedProcesses: {
        label: 'Processos Automatizados',
        structured: 'Processamento de Dados Estruturados',
        semiStructured: 'Análise de Dados Semiestruturados',
        unstructured: 'Extração de Dados Não Estruturados',
        multimedia: 'Análise de Imagem e Multimídia',
      },
      whyChooseUs: {
        chip: 'Por que KrElixir',
        title: 'Construído para Logística Empresarial',
        subtitle: 'Desenvolvido especialmente para JBS e operações logísticas empresariais. Nossa plataforma lida com a complexidade do processamento de documentos em múltiplos formatos em escala, com conformidade integrada e recursos de auditoria.',
        cta: 'Começar a processar documentos',
        benefit1Title: 'Saída de Dados Estruturados',
        benefit1Desc: 'Os dados extraídos são validados, estruturados e prontos para integração com ERP — sem limpeza manual.',
        benefit2Title: 'Análises em Tempo Real',
        benefit2Desc: 'Painéis ao vivo acompanham o volume de processamento, taxas de precisão e tendências de exceções.',
        benefit3Title: 'Arquitetura API-first',
        benefit3Desc: 'APIs RESTful para integração perfeita com SAP, Oracle e sistemas personalizados.',
        benefit4Title: 'SOC 2 Preparado',
        benefit4Desc: 'Infraestrutura Google Cloud com criptografia em repouso e em trânsito. Registro de auditoria completo.',
      },
      howItWorksAccuracy: {
        label: 'Precisão de Extração',
        poweredBy: 'desenvolvido por Google Document AI',
      },
    },
    // Navbar
    navbar: {
      features: 'Funcionalidades',
      howItWorks: 'Como Funciona',
      about: 'Sobre',
      login: 'Entrar',
      getStarted: 'Começar',
    },
    // Auth
    auth: {
      loginTitle: 'Bem-vindo de Volta',
      loginSubtitle: 'Entre na sua conta KrElixir',
      signupTitle: 'Criar Conta',
      signupSubtitle: 'Comece sua jornada de processamento de documentos',
      email: 'Endereço de Email',
      password: 'Senha',
      confirmPassword: 'Confirmar Senha',
      displayName: 'Nome Completo',
      loginButton: 'Entrar',
      signupButton: 'Criar Conta',
      forgotPassword: 'Esqueceu a Senha?',
      noAccount: 'Não tem uma conta?',
      hasAccount: 'Já tem uma conta?',
      signUp: 'Cadastre-se',
      signIn: 'Entrar',
      resetTitle: 'Redefinir Senha',
      resetSubtitle: 'Digite seu email e enviaremos um link de redefinição',
      resetButton: 'Enviar Link de Redefinição',
      resetSuccess: 'Email de redefinição enviado! Verifique sua caixa de entrada.',
      backToLogin: 'Voltar ao Login',
      logout: 'Sair',
      errors: {
        invalidEmail: 'Por favor, insira um endereço de email válido.',
        weakPassword: 'A senha deve ter pelo menos 6 caracteres.',
        passwordMismatch: 'As senhas não coincidem.',
        emailInUse: 'Este email já está cadastrado.',
        userNotFound: 'Nenhuma conta encontrada com este email.',
        wrongPassword: 'Senha incorreta. Tente novamente.',
        generic: 'Ocorreu um erro. Tente novamente.',
        nameRequired: 'Por favor, insira seu nome completo.',
      },
    },
    // Document Upload Page (Process 1)
    docUploadPage: {
      selectFromFirebase: 'Selecionar do Firebase',
      chooseExistingDoc: 'Escolha um documento enviado anteriormente para reprocessar',
      refreshDocList: 'Atualizar lista de documentos',
      loadingDocs: 'Carregando documentos...',
      selectADocument: 'Selecionar um documento',
      noneUploadNew: '— Nenhum (enviar novo arquivo) —',
      reprocessing: 'Reprocessando...',
      reprocessWithERP: 'Reprocessar com Comparação ERP',
      reprocessDesc: 'Executa comparação ERP e classificação nos dados extraídos existentes',
      orUploadNew: 'OU ENVIAR NOVO',
      deliveryNumber: 'Número de Entrega',
      deliveryNumberPlaceholder: 'Digite o número de entrega (opcional)',
      dropFileHere: 'Solte seu arquivo aqui, ou navegue',
      supportedFormats: 'Suporta PDF, JPG, JPEG, PNG — Armazenado no Firebase',
      removeFile: 'Remover arquivo',
      uploadingToFirebase: 'Enviando para o Firebase...',
      uploadAndExtract: 'Enviar e Extrair',
      processDocument: 'Processar Documento',
    },
    // Exception Queue Page
    exceptionQueuePage: {
      title: 'Fila de Exceções',
      subtitle: 'Revise e processe documentos que requerem atenção manual',
      pending: 'pendente(s)',
      noExceptions: 'Nenhum caso de exceção encontrado. Todos os documentos processados com sucesso!',
      documentId: 'ID do Documento',
      deliveryNumber: 'Número de Entrega',
      filename: 'Nome do Arquivo',
      exceptionReasons: 'Motivos da Exceção',
      uploadedAt: 'Enviado em',
      actions: 'Ações',
      review: 'Revisar',
      more: 'mais',
      reviewExceptionCase: 'Revisar Caso de Exceção',
      comparisonResults: 'Resultados da Comparação',
      field: 'Campo',
      document: 'Documento',
      erp: 'ERP',
      match: 'Correspondência',
      approving: 'Aprovando...',
      rejecting: 'Rejeitando...',
      rejectDailyFee: 'Rejeitar Taxa Diária',
      rejectionReason: 'Motivo da Rejeição',
      confirmReject: 'Confirmar Rejeição',
    },
    // Document Review Page
    documentReviewPage: {
      title: 'Revisão de Documentos',
      filterByStatus: 'Filtrar por Status',
      all: 'Todos',
      noDocuments: 'Nenhum documento encontrado',
      documentId: 'ID do Documento',
      filename: 'Nome do Arquivo',
      deliveryNumber: 'Número de Entrega',
      status: 'Status',
      uploadedAt: 'Enviado em',
      classification: 'Classificação',
      exception: 'Exceção',
      standard: 'Padrão',
      documentDetails: 'Detalhes do Documento',
      basicInfo: 'Informações Básicas',
      extractedData: 'Dados Extraídos',
      comparisonResults: 'Resultados da Comparação',
      exceptionCase: 'Caso de Exceção',
      standardCase: 'Caso Padrão',
      rejectDailyFee: 'Rejeitar Taxa Diária',
      rejectionReason: 'Motivo da Rejeição',
    },
    // In-App Footer
    appFooter: {
      allRightsReserved: 'Todos os direitos reservados',
      tagline: 'Transformação Digital Através de Dados e Soluções em Nuvem',
      website: 'Website',
      cloudSolutions: 'Soluções em Nuvem',
      dataSolutions: 'Soluções em Dados',
    },
    // Unloading Receipt Page (Process 2)
    receiptPage: {
      title: 'Upload de Recibo de Descarga',
      subtitle: 'Etapa 2: Envie e valide recibos de pagamento de descarga',
      orderNumber: 'Número do Pedido',
      orderNumberPlaceholder: 'Digite o número do pedido (opcional)',
      dropReceipt: 'Solte seu recibo aqui',
      dragDropReceipt: 'Arraste e solte seu recibo aqui',
      browseFormats: 'ou clique para navegar • PDF, JPG, PNG suportados',
      uploading: 'Enviando...',
      uploadReceipt: 'Enviar Recibo',
      processReceipt: 'Processar Recibo',
      processingSteps: 'Etapas de Processamento',
      validationResult: 'Resultado da Validação',
      valid: 'Válido',
      needsReview: 'Necessita Revisão',
      issues: 'Problemas',
      missingFields: 'Campos ausentes',
      statusRecommendation: 'Recomendação de Status',
      confidence: 'Confiança',
      rulesApplied: 'Regras Aplicadas',
      reasoning: 'Justificativa',
      approveReceipt: 'Aprovar Recibo',
      rejectReceipt: 'Rejeitar Recibo',
      extractedData: 'Dados Extraídos',
      fields: 'campos',
    },
    // Pallet Voucher Page (Process 3)
    palletPage: {
      title: 'Gestão de Vale Palete',
      subtitle: 'Etapa 3: Envie vales, execute reconciliação e gere relatórios',
      dragDropVoucher: 'Arraste e solte seu vale palete aqui',
      dropVoucher: 'Solte seu vale aqui',
      browseFormats: 'ou clique para navegar • PDF, JPG, PNG suportados',
      uploadProcess: 'Enviar e Processar Vale',
    },
    // Vehicle Registration Page (Process 4)
    vehiclePage: {
      title: 'Registro de Veículo de Transportadora',
      subtitle: 'Envie fotos ou documentos de veículos para análise automática. O sistema extrairá características do veículo usando visão computacional e comparará com dados de registro do ERP.',
      photoMode: 'Modo Foto (Visão Computacional)',
      documentMode: 'Modo Documento (OCR)',
      licensePlate: 'Placa (Opcional - para consulta ERP)',
      licensePlateHelper: 'Digite a placa conhecida para comparar com o registro ERP',
      selectPhoto: 'Selecionar Foto ou Documento do Veículo',
      analyzeVehicle: 'Analisar Veículo',
      analyzing: 'Analisando...',
    },
  },
};

export type TranslationKeys = typeof translations.en;
